import React from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import {
  ArrowUpRight,
  Briefcase,
  Users,
  GraduationCap,
  HeartHandshake,
  ShieldCheck,
  Target,
} from "lucide-react";

export default function Careers() {
  const opportunities = [
    {
      title: "Plant Operations",
      desc: "Work with advanced batching systems and production processes in a disciplined operational environment.",
      icon: Briefcase,
    },
    {
      title: "Quality & Technical",
      desc: "Join quality control and technical teams focused on precision, testing, and performance assurance.",
      icon: ShieldCheck,
    },
    {
      title: "Logistics & Dispatch",
      desc: "Coordinate fleet movement, dispatch planning, and timely project execution across sites.",
      icon: Target,
    },
    {
      title: "Management & Support",
      desc: "Build your career in administration, coordination, customer support, and operational management.",
      icon: Users,
    },
  ];

  const values = [
    "Professional Growth",
    "Structured Training",
    "Safe Working Environment",
    "Performance Driven Culture",
    "Team Collaboration",
    "Long-Term Career Opportunities",
  ];

  return (
    <main>
      {/* HERO */}
      <PageHero
        title="Careers"
        subtitle="Building strong structures begins with building strong people."
        num="09"
      />

      {/* INTRO */}
      <section className="py-24 bg-[#fafafa] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />

        <div className="max-w-[1500px] mx-auto px-5 md:px-8 relative">

          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* LEFT */}
            <div>

              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#ececef] bg-white mb-8">
                <div className="w-2 h-2 rounded-full bg-[#ef2435]" />

                <span className="text-[11px] uppercase tracking-[0.3em] text-[#ef2435] font-semibold">
                  Careers at SSC READYMIX
                </span>
              </div>

              <h2 className="text-5xl md:text-7xl font-black leading-[0.95] text-gray-900 mb-8">
                BUILD YOUR
                <br />
                FUTURE WITH
                <span className="text-[#ef2435]"> US.</span>
              </h2>

              <p className="text-gray-600 text-lg leading-[2] mb-8">
                At SSC READYMIX, we believe growth is driven by people,
                discipline, and opportunity. Our organization is committed
                to creating meaningful employment opportunities while
                empowering youth through structured learning, operational
                exposure, and long-term career development.
              </p>

              <p className="text-gray-600 text-lg leading-[2]">
                We are building a workplace focused on responsibility,
                teamwork, technical excellence, and continuous improvement
                across every level of operations.
              </p>

              <div className="flex flex-wrap gap-4 mt-10">

                <Link
                  to="/contact"
                  className="bg-[#ef2435] hover:bg-[#d91f31] text-white px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2 transition-all shadow-xl"
                >
                  Apply Now
                  <ArrowUpRight size={18} />
                </Link>

                <Link
                  to="/about"
                  className="bg-white border border-[#ececef] px-8 py-4 rounded-full font-semibold text-gray-700 hover:border-[#ef2435] hover:text-[#ef2435] transition-all"
                >
                  Learn More
                </Link>

              </div>

            </div>

            {/* RIGHT */}
            <div className="relative">

              <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full bg-[#ef2435]/5 blur-3xl" />

              <div className="grid sm:grid-cols-2 gap-5 relative">

                {[
                  {
                    title: "Youth Empowerment",
                    icon: HeartHandshake,
                  },
                  {
                    title: "Training & Development",
                    icon: GraduationCap,
                  },
                  {
                    title: "Professional Culture",
                    icon: Users,
                  },
                  {
                    title: "Career Growth",
                    icon: Target,
                  },
                ].map((item, i) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={i}
                      className="group bg-white border border-[#ececef] rounded-[30px] p-8 hover:border-[#ef2435] hover:-translate-y-2 hover:shadow-2xl transition-all duration-500"
                    >

                      <div className="w-14 h-14 rounded-2xl bg-[#ef2435]/10 flex items-center justify-center mb-6 group-hover:bg-[#ef2435] transition-all">

                        <Icon
                          size={26}
                          className="text-[#ef2435] group-hover:text-white transition-all"
                        />

                      </div>

                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        {item.title}
                      </h3>

                      <p className="text-gray-600 leading-relaxed">
                        Empowering individuals through structured opportunities,
                        learning, teamwork, and long-term career pathways.
                      </p>

                    </div>
                  );
                })}

              </div>

            </div>

          </div>

        </div>
      </section>

      {/* OPPORTUNITIES */}
      <section className="py-24 bg-white">

        <div className="max-w-[1500px] mx-auto px-5 md:px-8">

          <div className="mb-16 text-center">

            <span className="section-eyebrow justify-center inline-flex">
              [ OPPORTUNITIES ]
            </span>

            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mt-5">
              CAREER PATHWAYS.
            </h2>

          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">

            {opportunities.map((item, i) => {
              const Icon = item.icon;

              return (
                <div
                  key={i}
                  className="group rounded-[32px] border border-[#ececef] bg-[#fafafa] p-8 hover:border-[#ef2435] hover:bg-white hover:-translate-y-2 hover:shadow-2xl transition-all duration-500"
                >

                  <div className="flex items-center justify-between mb-8">

                    <div className="w-14 h-14 rounded-2xl bg-[#ef2435] flex items-center justify-center shadow-lg shadow-red-500/20">

                      <Icon size={24} className="text-white" />

                    </div>

                    <span className="text-[11px] font-semibold tracking-[0.25em] text-[#ef2435]">
                      0{i + 1}
                    </span>

                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {item.title}
                  </h3>

                  <p className="text-gray-600 leading-[1.9]">
                    {item.desc}
                  </p>

                </div>
              );
            })}

          </div>

        </div>

      </section>

      {/* CULTURE */}
      <section className="py-24 bg-[#111] text-white relative overflow-hidden">

        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#ef2435]/10 rounded-full blur-3xl" />

        <div className="max-w-[1300px] mx-auto px-5 md:px-8 relative">

          <div className="text-center mb-16">

            <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-white/10 bg-white/5 text-[11px] tracking-[0.3em] uppercase text-[#ef2435] font-semibold">
              Company Culture
            </span>

            <h2 className="text-5xl md:text-6xl font-black mt-6 leading-tight">
              PEOPLE.
              <span className="text-[#ef2435]"> PURPOSE.</span>
              PERFORMANCE.
            </h2>

          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">

            {values.map((item, i) => (
              <div
                key={i}
                className="bg-white/5 border border-white/10 rounded-[28px] p-7 backdrop-blur hover:border-[#ef2435] hover:bg-white/10 transition-all duration-500"
              >

                <div className="flex items-center gap-4">

                  <div className="w-10 h-10 rounded-full bg-[#ef2435] flex items-center justify-center text-sm font-bold">
                    {String(i + 1).padStart(2, "0")}
                  </div>

                  <h3 className="text-xl font-semibold">
                    {item}
                  </h3>

                </div>

              </div>
            ))}

          </div>

        </div>

      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-[#fafafa]">

        <div className="max-w-[1100px] mx-auto px-5 md:px-8 text-center">

          <span className="section-eyebrow justify-center inline-flex">
            [ JOIN SSC READYMIX ]
          </span>

          <h2 className="text-5xl md:text-7xl font-black leading-[0.95] text-gray-900 mt-6 mb-8">
            BUILD CAREERS.
            <br />
            BUILD CONFIDENCE.
            <br />
            BUILD THE FUTURE.
          </h2>

          <p className="text-gray-600 text-lg leading-[2] max-w-3xl mx-auto mb-10">
            SSC READYMIX continues to create opportunities for talented,
            hardworking, and growth-oriented individuals who want to build
            meaningful careers in the construction and infrastructure sector.
          </p>

          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#ef2435] hover:bg-[#d91f31] text-white px-10 py-5 rounded-full font-semibold transition-all shadow-2xl"
          >
            Submit Your Profile
            <ArrowUpRight size={18} />
          </Link>

        </div>

      </section>
    </main>
  );
}